<header class="go-page-title">
  <h1 class="go-heading-1">{{ pageTitle }}</h1>
  <a
  [href]="linkToSource"
  target="_blank">
    <go-button buttonVariant="neutral"> View on GitHub </go-button>
  </a>
</header>

<div class="go-container">

  <go-card class="go-column go-column--100" id="usage">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Usage</h2>
      <go-copy cardId="usage" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>
      <div class="go-column go-column--100">
        <!-- Intro -->
        <p class="go-body-copy">
          The <code class="code-block--inline">&lt;go-button&gt;</code>
          component should be used for any instance of a button throughout
          the app.
        </p>
      </div>

      <div class="go-column go-column--100">
        <!-- Bindings -->
        <h3 class="go-heading-3">Bindings</h3>
        <code [highlight]="componentBindings"></code>
      </div>

      <div class="go-column go-column--50 go-column--no-padding">
        <div class="go-container">

          <div class="go-column go-column--100">
            <h4 class="go-heading-4">buttonDisabled</h4>
            <p class="go-body-copy">
              Disables the button. This should be handled by the parent
              component.
            </p>
          </div>

          <div class="go-column go-column--100">
            <h4 class="go-heading-4">buttonIcon</h4>
            <p class="go-body-copy">
              Add an icon to the left of the text in the button. See the
              <a class="go-link" href="https://material.io/tools/icons/">Material Design System Icons</a>
              page for available icons.
            </p>
          </div>

          <div class="go-column go-column--100">
            <h4 class="go-heading-4">buttonType</h4>
            <p class="go-body-copy">
              Changes the type attribute of the button. This attribute
              defaults to <code class="code-block--inline">button</code> but other options are necessary when
              doing things like submitting (<code class="code-block--inline">submit</code>)
              or resetting (<code class="code-block--inline">reset</code>) forms.
            </p>
          </div>

          <div class="go-column go-column--100">
            <h4 class="go-heading-4">buttonVariant</h4>
            <p class="go-body-copy">
              The <code class="code-block--inline">buttonVariant</code>
              binding controls the style of the button. This includes
              options that allow us to implement the following
              modifications:
            </p>
            <ol class="go-list go-list--ordered">
              <li>Primary (default): <code class="code-block--inline">primary</code></li>
              <li>Secondary: <code class="code-block--inline">secondary</code></li>
              <li>Tertiary: <code class="code-block--inline">tertiary</code></li>
              <li>Negative: <code class="code-block--inline">negative</code></li>
              <li>Neutral: <code class="code-block--inline">neutral</code></li>
            </ol>
          </div>

        </div>
      </div>

      <div class="go-column go-column--50 go-column--no-padding">
        <div class="go-container">

          <div class="go-column go-column--100">
            <h4 class="go-heading-4">isProcessing</h4>
            <p class="go-body-copy">
              Turns the loading state on/off. It is recommended to use this when
              processing data that will take longer than ~250ms. Examples:
              submitting a form, sorting data, refreshing data from the
              server, etc.
            </p>
          </div>

          <div class="go-column go-column--100">
            <h4 class="go-heading-4">splitButtonOptions</h4>
            <p class="go-body-copy">
              Turns the button into a split-button, where the left side
              functions as a normal button and right side opens a dropdown
              menu with options for alternative actions.
            </p>
          </div>

          <div class="go-column go-column--100">
            <h4 class="go-heading-4">useDarkTheme</h4>
            <p class="go-body-copy">
              Applies a dark theme to the button component.
              The dark button styles can be applied by setting the
              <code class="code-block--inline">useDarkTheme</code>
              binding to <code class="code-block--inline">true</code>.
            </p>
          </div>

          <div class="go-column go-column--100">
            <h4 class="go-heading-4">handleClick</h4>
            <p class="go-body-copy">
              This event fires when the button is clicked. Use this event
              to handle the click event.
            </p>
          </div>

        </div>
      </div>

    </div>
  </go-card>

  <div class="go-column go-column--100">
    <go-toast
      type="negative"
      header="Note!"
      [message]="variantNotice">
    </go-toast>
  </div>

  <go-card class="go-column go-column--100" id="primary-buttons">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Primary Buttons</h2>
      <go-copy cardId="primary-buttons" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <p class="go-body-copy">
        The Primary button is the default button variant and should be used to indicate
        the primary action on a page, such as a "save" button.
      </p>
      <code [highlight]="primaryExample"></code>
      <div class="go-container">
        <div class="go-column go-column--25">
          <go-button (handleClick)="testClick()">Primary</go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button (handleClick)="testClick()" buttonDisabled="true">Disabled</go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button (handleClick)="testClick()" buttonIcon="work">With Icon</go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button (handleClick)="testClick()" buttonIcon="work"></go-button> Icon Only
        </div>
      </div>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--100" id="secondary-buttons">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Secondary Buttons</h2>
      <go-copy cardId="secondary-buttons" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <p class="go-body-copy">
        The Secondary button variant should be used to indicate a secondary action
        on a page. Examples could be: a refresh, opening modals, etc.
      </p>
      <code [highlight]="secondaryExample"></code>
      <div class="go-container">
        <div class="go-column go-column--25">
          <go-button buttonVariant="secondary" (handleClick)="testClick()">Secondary</go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button buttonVariant="secondary" (handleClick)="testClick()" buttonDisabled="true">Disabled</go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button buttonVariant="secondary" (handleClick)="testClick()" buttonIcon="work">With Icon</go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button buttonVariant="secondary" (handleClick)="testClick()" buttonIcon="work"></go-button> Icon Only
        </div>
      </div>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--100" id="tertiary-buttons">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Tertiary Buttons</h2>
      <go-copy cardId="tertiary-buttons" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <p class="go-body-copy">
        The Tertiary button variant should be used after both a primary and secondary variants have been used.
        It should generally only appear next to secondary variant buttons.
      </p>
      <code [highlight]="tertiaryExample"></code>
      <div class="go-container">
        <div class="go-column go-column--25">
          <go-button buttonVariant="tertiary" (handleClick)="testClick()">Tertiary</go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button buttonVariant="tertiary" (handleClick)="testClick()" buttonDisabled="true">Disabled</go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button buttonVariant="tertiary" (handleClick)="testClick()" buttonIcon="work">With Icon</go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button buttonVariant="tertiary" (handleClick)="testClick()" buttonIcon="work"></go-button> Icon Only
        </div>
      </div>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--100" id="negative-buttons">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Negative Buttons</h2>
      <go-copy cardId="negative-buttons" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <p class="go-body-copy">
        Negative buttons are displayed in red. Use negative buttons to designate
        an action as being dangerous or negative. Deleting a resource
        is a good example of when to use this button. It could also be
        used to guide a user away from an action we don't want them to
        take.
      </p>
      <code [highlight]="negativeExample"></code>
      <div class="go-container">
        <div class="go-column go-column--25">
          <go-button (handleClick)="testClick()" buttonVariant="negative">Negative</go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button (handleClick)="testClick()" buttonVariant="negative" buttonDisabled="true">Disabled</go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button (handleClick)="testClick()" buttonVariant="negative" buttonIcon="delete">With Icon</go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button (handleClick)="testClick()" buttonVariant="negative" buttonIcon="work"></go-button> Icon Only
        </div>
      </div>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--100" id="neutral-buttons">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Neutral Buttons</h2>
      <go-copy cardId="neutral-buttons" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <p class="go-body-copy">
        Neutral buttons are displayed in purple. Use neutral buttons to designate
        an action as being informational. Opening up a chat modal or
        sending a user to an external resource might be a good example
        of when to use this button. It could also be used to guide a
        user towards an action that isn't necessarily required, but still
        important.
      </p>
      <code [highlight]="neutralExample"></code>
      <div class="go-container">
        <div class="go-column go-column--25">
          <go-button (handleClick)="testClick()" buttonVariant="neutral">Neutral</go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button (handleClick)="testClick()" buttonVariant="neutral" buttonDisabled="true">Disabled</go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button (handleClick)="testClick()" buttonVariant="neutral" buttonIcon="live_help">With Icon</go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button (handleClick)="testClick()" buttonVariant="neutral" buttonIcon="work"></go-button> Icon Only
        </div>
      </div>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--100" id="loading-buttons">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Button with loader</h2>
      <go-copy cardId="loading-buttons" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <p class="go-body-copy">
        Loading buttons display the loading animation inside the
        button.  By default this is false. It is recommended to use
        this when processing data that will take longer than ~250ms.
        Examples might include submitting a form, sorting data,
        refreshing data from the server, etc. While in this state,
        a button will additionally be disabled to prevent the action
        from being taken more than once.
      </p>
      <h4 class="go-heading-4">example.ts</h4>
      <code [highlight]="loadingExampleTS"></code>
      <h4 class="go-heading-4">example.html</h4>
      <code [highlight]="loadingExampleHTML"></code>
      <h4 class="go-heading-4">View</h4>
      <div class="go-container">
        <div class="go-column go-column--50">
          <go-button
            (handleClick)="testSubmit('primaryButton')"
            [isProcessing]="primaryButtonLoading"
            buttonIcon="close"
            #primaryButton>
            Primary
          </go-button>
        </div>
        <div class="go-column go-column--50">
          <go-button
            (handleClick)="testSubmit('secondaryButton')"
            [isProcessing]="secondaryButtonLoading"
            buttonIcon="check"
            buttonVariant="secondary"
            #secondaryButton>
            Secondary
          </go-button>
        </div>
        <div class="go-column go-column--50">
          <go-button
            (handleClick)="testSubmit('tertiaryButton')"
            [isProcessing]="tertiaryButtonLoading"
            buttonIcon="check"
            buttonVariant="tertiary"
            #tertiaryButton>
            Tertiary
          </go-button>
        </div>
        <div class="go-column go-column--50">
          <go-button
            (handleClick)="testSubmit('negativeButton')"
            [isProcessing]="negativeButtonLoading"
            buttonIcon="delete"
            buttonVariant="negative"
            #negativeButton>
            Negative Loading
          </go-button>
        </div>
        <div class="go-column go-column--50">
          <go-button
            (handleClick)="testSubmit('neutralButton')"
            [isProcessing]="neutralButtonLoading"
            buttonIcon="info"
            buttonVariant="neutral"
            #neutralButton>
            Neutral Loading
          </go-button>
        </div>
        <div class="go-column go-column--50">
          <go-button
            (handleClick)="testSubmit('neutralButtonIcon')"
            [isProcessing]="neutralButtonIconLoading"
            buttonIcon="info"
            buttonVariant="neutral"
            #neutralButtonIcon>
          </go-button>
          Neutral, Icon Only Loading
        </div>
      </div>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--100" id="dark-buttons">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Dark buttons</h2>
      <go-copy cardId="dark-buttons" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <div class="go-container">
        <div class="go-column go-column--100 go-column--no-padding">
          <p class="go-body-copy">
            Dark buttons work all the same as the rest of the buttons,
            the difference being that some of the colors have been tweaked
            to allow this button to maintain an appropriate amount of
            contrast when displayed on a dark background.
          </p>
          <p class="go-body-copy">
            The dark button styles can be applied by setting the
            <code class="code-block--inline">useDarkTheme</code>
            binding to <code class="code-block--inline">true</code>.
          </p>

          <code [highlight]="darkButtonExample"></code>
        </div>
        <div class="go-column go-column--100 go-column--no-padding">
          <div class="dark-example-area">
            <div class="go-container">
              <div class="go-column go-column--50">
                <go-button (handleClick)="testClick()" [useDarkTheme]="true">
                  Primary
                </go-button>
              </div>
              <div class="go-column go-column--50">
                <go-button
                  (handleClick)="testSubmit('secondaryDarkButton')"
                  [useDarkTheme]="true"
                  buttonVariant="secondary"
                  buttonIcon="home"
                  [isProcessing]="secondaryDarkButtonLoading"
                  #secondaryDarkButton>
                  Secondary
                </go-button>
              </div>
              <div class="go-column go-column--50">
                <go-button
                  (handleClick)="testSubmit('tertiaryDarkButton')"
                  [useDarkTheme]="true"
                  buttonVariant="tertiary"
                  buttonIcon="home"
                  [isProcessing]="tertiaryDarkButtonLoading"
                  #tertiaryDarkButton>
                  Tertiary
                </go-button>
              </div>
              <div class="go-column go-column--50">
                <go-button
                  (handleClick)="testClick()"
                  [useDarkTheme]="true"
                  buttonVariant="neutral"
                  buttonIcon="live_help">
                  With Icon
                </go-button>
              </div>
              <div class="go-column go-column--50">
                <go-button
                  (handleClick)="testSubmit('negativeDarkButton')"
                  [useDarkTheme]="true"
                  buttonIcon="delete"
                  buttonVariant="negative"
                  [isProcessing]="negativeDarkButtonLoading"
                  #negativeDarkButton>
                  Negative Loading
                </go-button>
              </div>
              <div class="go-column go-column--50">
                <go-button
                  [useDarkTheme]="true"
                  buttonIcon="check"
                  buttonDisabled="true">
                  Disabled
                </go-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--100" id="button-group">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Button Groups</h2>
      <go-copy cardId="button-group" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <div class="go-container">
        <div class="go-column go-column--100 go-column--no-padding">
          <p class="go-body-copy">
            Sometimes it will be necessary to group buttons together. There are a set of helper
            classes available to accomplish this.
          </p>
          <code [highlight]="buttonGroup"></code>
        </div>
        <div class="go-column go-column--100 go-column--no-padding">
          <ul class="go-button-group">
            <li class="go-button-group__item">
              <go-button>Button 1</go-button>
            </li>
            <li class="go-button-group__item">
              <go-button>Button 2</go-button>
            </li>
          </ul>
        </div>
      </div>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--100" id="split-buttons">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Split Buttons</h2>
      <go-copy cardId="split-buttons" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <p class="go-body-copy">
        The <code class="code-block--inline">@Input() splitButtonOptions: SplitButtonOption[]</code>
        input allows you to turn the button into a split-button.
      </p>

      <p class="go-body-copy">
        The interface for the options looks like this:
      </p>
      <code [highlight]="splitButtonOptionInterface"></code>

      <p class="go-body-copy">
        The <code class="code-block--inline">label</code> of an option will determine the
        text displayed for that option in the split-button's dropdown menu.
      </p>

      <p class="go-body-copy">
        The <code class="code-block--inline">action</code> of an option is the function
        that will be called when the action is clicked.
        In order for the function to be able to reference properties on the component
        class on which it is defined and to be able to receive arguments, you must bind
        <code class="code-block--inline">this</code> to the context of the function (see below).
      </p>

      <h4 class="go-heading-4">example.ts</h4>
      <code [highlight]="splitExampleTS"></code>

      <h4 class="go-heading-4">example.html</h4>
      <code [highlight]="splitExampleHTML"></code>

      <caption>
        <strong>NOTE:</strong> Currently, only the Primary and Secondary button types
        support split-button functionality.
      </caption>

      <div class="go-container">
        <div class="go-column go-column--25">
          <go-button
            (handleClick)="testClick()"
            [splitButtonOptions]="splitButtonOptions">
            Primary
          </go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button
            (handleClick)="testClick()"
            [splitButtonOptions]="splitButtonOptions"
            buttonDisabled="true">
            Disabled
          </go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button
            (handleClick)="testClick()"
            [splitButtonOptions]="splitButtonOptions"
            buttonIcon="work">
            With Icon
          </go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button
            (handleClick)="testClick()"
            [splitButtonOptions]="splitButtonOptions"
            buttonIcon="work">
          </go-button> Icon Only
        </div>
        <div class="go-column go-column--25">
          <go-button
            (handleClick)="testClick()"
            [splitButtonOptions]="splitButtonOptions"
            buttonVariant="secondary">
            Secondary
          </go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button
            (handleClick)="testClick()"
            [splitButtonOptions]="splitButtonOptions"
            buttonVariant="secondary"
            buttonDisabled="true">
            Disabled
          </go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button
            (handleClick)="testClick()"
            [splitButtonOptions]="splitButtonOptions"
            buttonVariant="secondary"
            buttonIcon="work">
            With Icon
          </go-button>
        </div>
        <div class="go-column go-column--25">
          <go-button
            (handleClick)="testClick()"
            [splitButtonOptions]="splitButtonOptions"
            buttonVariant="secondary"
            buttonIcon="work">
          </go-button> Icon Only
        </div>
      </div>
    </ng-container>
  </go-card>
</div>
